import { z } from "zod";

// A merge marker carries no text and draws as an empty cell continuing the one
// above or to its left.
const GrammarTableCellSchema = z.object({
  kind: z.string(),
  text: z.string().optional(),
});

const GrammarTableSchema = z.object({
  heading: z.string().optional(),
  rows: z.array(z.array(GrammarTableCellSchema)),
});

// `type` is a free string upstream, not an enum — a book introducing a new kind
// must not lose its rows here. Passthrough for the same reason: unlisted fields
// are kept rather than silently stripped.
export const BookLessonRowSchema = z
  .object({
    type: z.string(),
    position: z.number().int().nonnegative(),
    text: z.string(),
    partOfSpeech: z.string().optional(),
    example: z.string().optional(),
    // One value per lesson, repeated on each row that carries it.
    lessonContext: z.string().optional(),
    description: z.string().optional(),
    grammarNote: z.string().optional(),
    note: z.string().optional(),
    // The part this row is printed under, where the book lists it apart from the lesson's main
    // vocabulary ("Real life"). Matched against a section scope case-insensitively.
    subList: z.string().optional(),
    tables: z.array(GrammarTableSchema).optional(),
    // The source repeats the lesson's own address on every row.
    levelId: z.string().optional(),
    componentId: z.string().optional(),
    lesson: z.number().int().optional(),
  })
  .passthrough();

export const BookLessonPageLineSchema = z
  .object({
    kind: z.string(),
    text: z.string(),
    // The source's heading for the run of lines this one sits in; captures taken before it
    // existed carry none.
    section: z.string().optional(),
  })
  .passthrough();

// What the book prints at the head of the unit, and a lesson's identity — array position is not:
// a capture that starts counting reviews, or splits a lesson into A/B halves, renumbers every
// position after it, while "Lesson 2B" stays "Lesson 2B". `kind` is "lesson" or "review" today but
// stays a free string, as with row types. `numberText` holds a zero-padded printing ("01").
export const BookLessonPrintedSchema = z.object({
  kind: z.string().min(1),
  heading: z.string().min(1),
  // Absent where the book prints none: "Songs", or a "Review" numbered only by its unit.
  number: z.number().int().positive().optional(),
  numberText: z.string().optional(),
  // The printed unit a lesson sits in, where a book restarts its numbering in each.
  unit: z.number().int().positive().optional(),
  part: z.string().optional(),
});

/**
 * The unit's name as the book prints it — "Lesson 03", "Lesson 2B", "Unit 2 · Lesson 1", "Songs".
 * A one-letter part joins the number; a longer one is a section and is set apart.
 */
export function bookLessonLabel(printed: z.infer<typeof BookLessonPrintedSchema>): string {
  const unit = printed.unit === undefined ? "" : `Unit ${printed.unit} · `;
  const number =
    printed.number === undefined ? "" : ` ${printed.numberText ?? String(printed.number)}`;
  const part =
    printed.part === undefined
      ? ""
      : /^[A-Z]$/u.test(printed.part) && number !== ""
        ? printed.part
        : ` · ${printed.part}`;
  return `${unit}${printed.heading}${number}${part}`;
}

export const BookLessonSchema = z.object({
  seriesSlug: z.string().min(1),
  levelId: z.string().min(1),
  levelName: z.string().min(1),
  // Book order is array position at the source; captured so one flat collection stays orderable.
  levelOrder: z.number().int().nonnegative(),
  componentId: z.string().min(1),
  componentName: z.string().min(1),
  componentOrder: z.number().int().nonnegative(),
  // Position in the book, which moves between captures. Order by it; never address by it.
  lesson: z.number().int().positive(),
  printed: BookLessonPrintedSchema,
  // Denormalised from `printed` so aggregations and callers share one spelling of the name.
  printedLabel: z.string().min(1),
  rows: z.array(BookLessonRowSchema).default([]),
  pageLines: z.array(BookLessonPageLineSchema).default([]),
  // Above pageLines.length means the source truncated its reading.
  pageLineCount: z.number().int().nonnegative().default(0),
});

export type BookLessonRow = z.infer<typeof BookLessonRowSchema>;
export type BookLessonPageLine = z.infer<typeof BookLessonPageLineSchema>;
export type BookLessonPrinted = z.infer<typeof BookLessonPrintedSchema>;
export type BookLesson = z.infer<typeof BookLessonSchema>;
